import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Redirect, Route, useHistory, useLocation, useParams } from "react-router-dom"
import { FullScreenLoader } from "~Components/FullScreenLoader"
import { Main } from "~Components/layout/Main"
import { FeatureDeactivatedPage } from "~Features/errors/FeatureDeactivatedPage"
import { NotFoundPage } from "~Features/errors/NotFoundPage"
import { LoggedInEmployeeProvider } from "~Shared/context/loggedInEmployee.context"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { useCompanies, useUserCompanies } from "~Shared/hooks/company.hooks"
import { useIsFreeVersion } from "~Shared/hooks/featureFlags.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { LocalStorageService, NotificationService } from "~Shared/services"
import { ACCESS_TOKEN } from "~Shared/services/localstorage.consts"
import { SessionStorageService } from "~Shared/services/sessionstorage.service"
import { selectCompany } from "~Shared/store/actions/company/companyActions"
import { decodeAccessToken } from "~Shared/utils/auth.utils"
import { generatePath } from "~Shared/utils/routing.utils"
import { ErrorRoute } from "./ErrorRoute"
import { CompanyScopedRoutePathEnum, RoutePathEnum } from "./RoutePath.enum"

// Imports of shared modules
interface PrivateRouteProps extends FreeVersionAccessWrapperProps, LoginRedirectWrapperProps {
    path: string
    exact?: boolean
    canSee?: boolean
    isCompanyScoped?: boolean
    isFeatureEnabled?: boolean
}

interface LoginRedirectWrapperProps {
    children: React.ReactNode
    location?: Location
}

export type LoginRedirectState = {
    from?: string
    companyId?: string
}

/**
 * Wrapper component that checks authentication and redirects to login if needed
 * Uses useParams to extract companyId for company-scoped routes
 */
export const LoginRedirectWrapper = ({ children, location }: LoginRedirectWrapperProps) => {
    const routeLocation = useLocation()
    const { companyId } = useParams<{ companyId?: string }>()
    if (!LocalStorageService.isAuthorized()) {
        const isLogout = SessionStorageService.getIsLoggingOut()
        if (isLogout) {
            SessionStorageService.clearIsLoggingOut() // clear lock
            return (
                <Redirect
                    to={{
                        pathname: generatePath(RoutePathEnum.LOGIN),
                    }}
                />
            )
        }

        const state: LoginRedirectState = {
            from: location?.pathname || routeLocation.pathname,
            companyId,
        }
        return (
            <Redirect
                to={{
                    pathname: generatePath(RoutePathEnum.LOGIN),
                    state,
                }}
            />
        )
    }

    return children
}

interface FreeVersionAccessWrapperProps {
    children: React.ReactNode
    includedFreeVersion?: boolean
}

/**
 * Wrapper component that checks if the feature is accessible in free version
 * Shows NotFoundPage for paid features when tenant is on free version
 */
const FreeVersionAccessWrapper = ({ children, includedFreeVersion = false }: FreeVersionAccessWrapperProps) => {
    const isTenantOnFreeVersion = useIsFreeVersion()

    // if tenant of user is only allowed free version forbid access to paid features
    if (isTenantOnFreeVersion && !includedFreeVersion) {
        return (
            <ErrorRoute>
                <NotFoundPage />
            </ErrorRoute>
        )
    }

    // Feature is accessible, render children
    return <>{children}</>
}

const shouldSwitch = (urlCompanyId?: string, tokenCompanyId?: string) => {
    return urlCompanyId && tokenCompanyId && urlCompanyId !== tokenCompanyId
}

// watch for access token changes from other tabs
function useAccessTokenWatcher() {
    const history = useHistory()
    useEffect(() => {
        const handler = (event: StorageEvent) => {
            if (event.key !== ACCESS_TOKEN) {
                return
            }
            if (event.newValue && event.oldValue) {
                const newToken = decodeAccessToken(event.newValue)
                const oldToken = decodeAccessToken(event.oldValue)
                if (newToken.currentCompanyId && newToken.currentCompanyId !== oldToken.currentCompanyId) {
                    history.push(generatePath(CompanyScopedRoutePathEnum.EXPENSES, { companyId: newToken.currentCompanyId }))
                }
            }
        }
        window.addEventListener("storage", handler)
        return () => window.removeEventListener("storage", handler)
    }, [])
}

/**
 * Wrapper component for routes with company-scoped URLs
 * Handles company ID validation and automatic company switching
 */
const CompanyScopedWrapper = ({ children }: { children: any }) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const { companyId: urlCompanyId } = useParams<{ companyId?: string }>()
    const { companyId: tokenCompanyId } = useAuth()
    const companies = useCompanies()
    const loggedInEmployee = useLoggedInEmployeeProfile()
    const userCompanies = useUserCompanies(companies, loggedInEmployee)
    const [isSwitching, setIsSwitching] = useState(shouldSwitch(urlCompanyId, tokenCompanyId))

    useEffect(() => {
        // Detect what changed
        if (shouldSwitch(urlCompanyId, tokenCompanyId) && userCompanies?.length > 0) {
            const company = userCompanies.find(({ _id }) => _id === urlCompanyId)
            if (!company) {
                window.location.href = generatePath(RoutePathEnum.ROOT)
                return
            }
            setIsSwitching(true)
            selectCompany(company)(dispatch)
                .catch((err) => {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, "Error", "Failed to switch company")
                    window.location.href = generatePath(RoutePathEnum.ROOT)
                })
                .finally(() => {
                    setIsSwitching(false)
                })
        }
    }, [urlCompanyId, tokenCompanyId, location.pathname, userCompanies, dispatch, history])
    useAccessTokenWatcher()

    if (isSwitching || shouldSwitch(urlCompanyId, tokenCompanyId)) {
        return <FullScreenLoader />
    }

    return <>{children}</>
}

// check auth -> check company (switch if needed) -> check canSee -> check free version
export const PrivateRoute = ({
    path,
    canSee = true,
    includedFreeVersion = false,
    exact = false,
    children,
    location,
    isCompanyScoped = false,
    isFeatureEnabled = true,
}: PrivateRouteProps) => {
    // some features (e.g. p2p) can be disabled on a per-tenant basis
    const routeLocation = useLocation()

    const routeContent = canSee ? (
        <FreeVersionAccessWrapper includedFreeVersion={includedFreeVersion}>
            <LoggedInEmployeeProvider>
                <Main>{children}</Main>
            </LoggedInEmployeeProvider>
        </FreeVersionAccessWrapper>
    ) : (
        <>
            <Redirect
                to={{
                    pathname: generatePath(RoutePathEnum.ROOT),
                    state: { from: location?.pathname ?? routeLocation.pathname },
                }}
            />
        </>
    )

    return (
        <Route {...{ path, exact }}>
            <LoginRedirectWrapper location={location}>
                {!isFeatureEnabled ? (
                    <ErrorRoute>
                        <FeatureDeactivatedPage />
                    </ErrorRoute>
                ) : isCompanyScoped ? (
                    <CompanyScopedWrapper>{routeContent}</CompanyScopedWrapper>
                ) : (
                    routeContent
                )}
            </LoginRedirectWrapper>
        </Route>
    )
}
